import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaPlay, FaBook, FaGraduationCap, FaStar, FaClock, FaUsers, FaQuoteLeft } from 'react-icons/fa';
import { hafezLessonsData } from '../data/allLessons';
import type { HafezLesson } from '../data/allLessons';

const HafezLessons: React.FC = () => {
  const [completedLessons, setCompletedLessons] = useState<number[]>([]);
  const [totalProgress, setTotalProgress] = useState(0);

  useEffect(() => {
    // Load progress from localStorage
    const savedProgress = localStorage.getItem('hafez-progress');
    if (savedProgress) {
      const progress = JSON.parse(savedProgress);
      setCompletedLessons(progress.completedLessons || []);
      setTotalProgress(progress.totalProgress || 0);
    }
  }, []);

  const getLessonProgress = (lessonId: number): number => {
    return completedLessons.includes(lessonId) ? 100 : 0;
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'advanced': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getDifficultyText = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'مبتدی';
      case 'intermediate': return 'متوسط';
      case 'advanced': return 'پیشرفته';
      default: return difficulty;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <FaQuoteLeft className="text-4xl text-purple-500 mr-3" />
                      <h1 className="text-4xl font-bold text-white">
            Hafez Poetry
          </h1>
          </div>
          <h2 className="text-2xl font-semibold text-white mb-2">
            اشعار حافظ
          </h2>
          <p className="text-white max-w-2xl mx-auto">
            Read and understand the beautiful poetry of Hafez with line-by-line translations, 
            word-by-word analysis, and cultural context. Discover the mystical world of Persian poetry.
          </p>
        </div>

        {/* Progress Overview */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              Overall Progress
            </h3>
            <span className="text-2xl font-bold text-purple-500">
              {Math.round(totalProgress)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
            <div 
              className="bg-gradient-to-r from-purple-500 to-indigo-500 h-3 rounded-full transition-all duration-500"
              style={{ width: `${totalProgress}%` }}
            ></div>
          </div>
          <div className="flex justify-between mt-2 text-sm text-gray-600 dark:text-gray-400">
            <span>{completedLessons.length} of {hafezLessonsData.length} lessons completed</span>
            <span>{Math.round((completedLessons.length / hafezLessonsData.length) * 100)}% complete</span>
          </div>
        </div>

        {/* Lessons Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {hafezLessonsData.map((lesson: HafezLesson) => {
            const progress = getLessonProgress(lesson.id);
            const isCompleted = progress === 100;
            
            return (
              <div 
                key={lesson.id}
                className={`bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:scale-105 ${
                  isCompleted ? 'ring-2 ring-green-500' : ''
                }`}
              >
                {/* Lesson Header */}
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <span className="text-3xl mr-3">{lesson.icon}</span>
                      <div>
                        <h3 className="text-lg font-bold text-white">
                          {lesson.title}
                        </h3>
                        <p className="text-sm text-white">
                          {lesson.persianTitle}
                        </p>
                      </div>
                    </div>
                    {isCompleted && (
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                        <FaStar className="text-white text-sm" />
                      </div>
                    )}
                  </div>
                  
                  <p className="text-white text-sm mb-4">
                    {lesson.description}
                  </p>

                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-4">
                    <div 
                      className="bg-gradient-to-r from-purple-500 to-indigo-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm text-white">
                    <span>{progress}% complete</span>
                    <span>{lesson.poems.length} poems</span>
                  </div>
                </div>

                {/* Lesson Details */}
                <div className="p-6">
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center text-sm text-white">
                      <FaBook className="mr-2 text-purple-500" />
                      <span><strong>Focus:</strong> {lesson.focus}</span>
                    </div>
                    <div className="flex items-center text-sm text-white">
                      <FaGraduationCap className="mr-2 text-purple-500" />
                      <span><strong>Goal:</strong> {lesson.goal}</span>
                    </div>
                    <div className="flex items-center text-sm text-white">
                      <FaClock className="mr-2 text-purple-500" />
                      <span><strong>Estimated time:</strong> 30-45 minutes</span>
                    </div>
                    <div className="flex items-center text-sm text-white">
                      <FaUsers className="mr-2 text-purple-500" />
                      <span><strong>Level:</strong> {lesson.poems[0]?.difficulty || 'Beginner'}</span>
                    </div>
                  </div>

                  {/* Poems Preview */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-white mb-3">
                      Poems in this lesson:
                    </h4>
                    <div className="space-y-2">
                      {lesson.poems.map((poem) => (
                        <div key={poem.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <div>
                            <p className="text-sm font-medium text-white">
                              {poem.title}
                            </p>
                            <p className="text-xs text-white">
                              Theme: {poem.theme}
                            </p>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(poem.difficulty)}`}>
                            {getDifficultyText(poem.difficulty)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3">
                    <Link
                      to={`/hafez/lesson/${lesson.id}`}
                      className={`w-full flex items-center justify-center px-4 py-3 rounded-lg font-semibold transition-all duration-300 ${
                        isCompleted
                          ? 'bg-green-500 hover:bg-green-600 text-white'
                          : 'bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white'
                      }`}
                    >
                      <FaPlay className="mr-2" />
                      {isCompleted ? 'Review Lesson' : 'Start Lesson'}
                    </Link>
                    
                    <Link
                      to={`/hafez/flashcards/${lesson.id}`}
                      className="w-full flex items-center justify-center px-4 py-2 border-2 border-purple-500 text-purple-500 dark:text-purple-400 rounded-lg font-semibold hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all duration-300"
                    >
                      <FaBook className="mr-2" />
                      Flashcards
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 text-center shadow-lg">
            <div className="text-2xl font-bold text-purple-500 mb-1">
              {hafezLessonsData.flatMap(lesson => lesson.poems).length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Total Poems</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 text-center shadow-lg">
            <div className="text-2xl font-bold text-green-500 mb-1">
              {hafezLessonsData.length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Lessons</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 text-center shadow-lg">
            <div className="text-2xl font-bold text-blue-500 mb-1">
              {hafezLessonsData.flatMap(lesson => lesson.quiz).length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Quiz Questions</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 text-center shadow-lg">
            <div className="text-2xl font-bold text-indigo-500 mb-1">100%</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Audio Support</div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-purple-500 to-indigo-500 rounded-2xl p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-4">
            Ready to Explore Hafez's Poetry?
          </h3>
          <p className="text-purple-100 mb-6 max-w-2xl mx-auto">
            Begin your journey into the mystical world of Persian poetry. Each lesson includes 
            line-by-line translations, word-by-word analysis, and cultural context to help you 
            understand the deeper meanings.
          </p>
          <Link
            to="/hafez/lesson/1"
            className="inline-flex items-center px-8 py-3 bg-white text-purple-500 font-semibold rounded-lg hover:bg-purple-50 transition-all duration-300"
          >
            <FaPlay className="mr-2" />
            Start Learning Now
          </Link>
        </div>
      </div>
    </div>
  );
};

export default HafezLessons; 